import {
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  LazyLoader,
  MeetingActiveIcon,
} from '@bp/ui-components';
import classNames from 'classnames';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useConfirm } from 'hooks/useConfirm';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { copyEventLinkToClipboard, endEvent, joinEvent, startAndJoinEvent, startEvent } from 'utils/eventHelper';
import { lowercaseFirstLetter } from 'utils/stringHelper';
import styles from './TodayEvent.module.scss';

export type TodayEventType = {
  uuid: string;
  name: string;
  description: string | null;
  from: Date;
  to: Date;
  timeFrameLabel: string;
  virtual: boolean;
  running: boolean;
  canStartStopMeeting: boolean;
  eventLink: string | null;
  token: string | null;
  type: string;
};

type TodayEventProps = {
  event: TodayEventType;
  className?: string | undefined;
  refetchData?: () => void;
};

export const TodayEvent = ({ event, className, refetchData }: TodayEventProps) => {
  const { t } = useTranslation();
  const profile = useAuthClaims().pimAuthClaims.getProfile();

  const { name, description, timeFrameLabel, virtual, running, eventLink, token, type } = event;

  const [loading, setLoading] = useState<boolean>(false);

  const { ConfirmationDialog, confirm } = useConfirm({
    defaultTitle: `${t('meetings.title')} ${lowercaseFirstLetter(t('meetings.end'))}`,
    defaultMessage: t('meetings.confirmEnd'),
    defaultConfirmText: t('meetings.end'),
  });

  async function handleJoin() {
    setLoading(true);
    try {
      if (token) await joinEvent(token, profile);
    } catch (e) {
      console.log(e);
    }
    refetchData && refetchData();
    setLoading(false);
  }

  async function handleStart() {
    setLoading(true);
    if (token) await startEvent(token);
    refetchData && refetchData();
    setLoading(false);
  }

  async function handleStartAndJoin() {
    setLoading(true);
    if (token) await startAndJoinEvent(token, profile);
    refetchData && refetchData();
    setLoading(false);
  }

  async function handleEnd() {
    const result = await confirm();
    if (!result) return;
    setLoading(true);
    if (token) await endEvent(token);
    refetchData && refetchData();
    setLoading(false);
  }

  const menu: DropdownMenuItem[] = [
    { label: t('meetings.join'), onClick: handleJoin, disabled: !running },
    {
      label: t('meetings.start'),
      onClick: handleStart,
      disabled: running,
    },
    {
      label: t('meetings.startAndJoin'),
      onClick: handleStartAndJoin,
      disabled: running,
    },
    { type: 'ruler' },
    {
      label: t('meetings.copyLink'),
      onClick: () => eventLink && copyEventLinkToClipboard(eventLink),
      disabled: !eventLink,
    },
    { type: 'ruler' },
    {
      label: t('meetings.end'),
      color: 'error',
      onClick: handleEnd,
      disabled: !running,
    },
  ];

  const classes = classNames(styles['today-event'], className);

  return (
    <>
      <div className={classes}>
        {loading && <LazyLoader transparent className={styles.loader} />}
        <div className={styles.header}>
          <div className={styles.data}>
            <div className={styles.name} title={name}>
              {name}
            </div>
            <div className={styles.meta}>
              <div className={styles['time-frame']}>{timeFrameLabel}</div>
              <div className={styles.type}>{type}</div>
            </div>
          </div>
          <div className={styles.actions}>
            {!event.canStartStopMeeting && (
              <Button icon={running && <MeetingActiveIcon />} hierarchy={'secondary'} onClick={handleJoin}>
                Beitreten
              </Button>
            )}
            {event.canStartStopMeeting && (
              <>
                {running && (
                  <div className={styles.icon} title={t('meetings.running')}>
                    <MeetingActiveIcon />
                  </div>
                )}
                <Dropdown
                  disabled={!virtual || !event.canStartStopMeeting}
                  trigger={
                    <Button
                      icon={<DotsVerticalIcon className='small' />}
                      hierarchy='ghost'
                      disabled={!virtual || !event.canStartStopMeeting}
                      className='ml-1'
                    />
                  }
                >
                  <DropdownMenu data={menu} />
                </Dropdown>
              </>
            )}
          </div>
        </div>
        {description && (
          <div className={styles.description} title={description}>
            {description}
          </div>
        )}
      </div>

      <ConfirmationDialog />
    </>
  );
};
